import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import {
  EventsApiError,
  type EventsScope,
} from '../../features/events/api/types';
import {
  fetchApprovals,
  fetchEventsOrganizations,
  fetchEventsSession,
  fetchPipeline,
} from '../../features/events/client';
import { eventsKeys } from '../../features/events/queryKeys';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import {
  rememberEventsEnabled,
  useNavVisibility,
} from '../../layout/useNavVisibility';
import { api, apiEndpoints, getCurrentStoreId } from '../../utils/apiService';
import {
  fetchReservationsIntegrationStatus,
  reservationsStatusHasActiveConnection,
} from '../../utils/reservationsIntegrationStatus';
import {
  fetchOrdersIntegrationStatus,
  ordersStatusHasActiveConnection,
} from '../Integrations/integrationsModel';
import {
  type ApiInquiry,
  type ApiKnowledgeQuestion,
  type ApiSmsConversation,
  type ApiVoicemail,
  type AttentionGroup,
  type AttentionKind,
  type CallIntelligence,
  type ChannelCounts,
  type ChannelGating,
  type DashboardStats,
  type NeedsYou,
  type ProductGating,
  ACTIVITY_PAGE,
  ROWS_PER_GROUP,
  approvalItem,
  buildActivity,
  inquiryItem,
  knowledgeQuestionItem,
  paymentBookings,
  paymentItem,
  readStats,
  smsItem,
  voicemailItem,
} from './dashboardModel';

type Paginated<T> = { count?: number; results?: T[] };

async function getJson<T>(url: string, what: string): Promise<T> {
  const response = await api.get(url);
  if (!response?.ok) throw new Error(`${what} could not be loaded.`);
  return (await response.json()) as T;
}

/** A read whose failure should show as a dash, not fail the block. */
async function getJsonOrNull<T>(url: string): Promise<T | null> {
  const response = await api.get(url).catch(() => null);
  if (!response?.ok) return null;
  return ((await response.json().catch(() => null)) as T | null) ?? null;
}

const settled = <T>(r: PromiseSettledResult<T>): T | null =>
  r.status === 'fulfilled' ? r.value : null;

// ---------------------------------------------------------------------------
// This month
// ---------------------------------------------------------------------------

export interface DashboardFigures {
  stats: DashboardStats;
  gating: ProductGating;
  channels: ChannelGating;
  /** Recent calls, orders and reservations from the same list endpoints. */
  activity: ReturnType<typeof buildActivity>;
}

type CountPage = Record<string, unknown> | null;

/**
 * This month's and all-time `count` for each channel that is on, one row per
 * page. A failed count is a dash and never fails the strip.
 */
async function fetchChannelCounts(
  channels: ChannelGating,
): Promise<Partial<ChannelCounts>> {
  const counts: [keyof ChannelCounts, string][] = [
    [
      'voicemailsMonth',
      apiEndpoints.voicemails({
        folder: 'all',
        month: 'current',
        page_size: 1,
      }),
    ],
    ['voicemailsAll', apiEndpoints.voicemails({ folder: 'all', page_size: 1 })],
  ];
  if (channels.events) {
    counts.push(
      [
        'eventInquiriesMonth',
        apiEndpoints.getEventInquiries({
          dashboard_state: 'all',
          month: 'current',
          page_size: 1,
        }),
      ],
      [
        'eventInquiriesAll',
        apiEndpoints.getEventInquiries({
          dashboard_state: 'all',
          page_size: 1,
        }),
      ],
    );
  }
  if (channels.sms) {
    counts.push(
      [
        'smsMonth',
        apiEndpoints.smsConversations({ month: 'current', page_size: 1 }),
      ],
      ['smsAll', apiEndpoints.smsConversations({ page_size: 1 })],
    );
  }
  const pages = await Promise.all(
    counts.map(([, url]) => getJsonOrNull<CountPage>(url)),
  );
  return Object.fromEntries(counts.map(([key], i) => [key, pages[i]]));
}

/**
 * Production's three reads, the two integration statuses that decide
 * whether Orders and Reservations are shown, and a count per channel. One
 * failed read is a dash for its figures; only when the three main reads all
 * fail does the block show Retry.
 */
async function fetchFigures(
  channels: ChannelGating,
): Promise<DashboardFigures> {
  const [calls, orders, reservations, pos, openTable, recentOrders, counts] =
    await Promise.allSettled([
      getJsonOrNull<Record<string, unknown>>(
        apiEndpoints.getCalls(1, ACTIVITY_PAGE, true, undefined, '-start_time'),
      ),
      getJsonOrNull<Record<string, unknown>>(apiEndpoints.getOrdersCount()),
      getJsonOrNull<Record<string, unknown> | unknown[]>(
        `${apiEndpoints.openTableDashboardReservations()}?page=1&page_size=${ACTIVITY_PAGE}&ordering=-updated_at`,
      ),
      fetchOrdersIntegrationStatus(),
      fetchReservationsIntegrationStatus(),
      // The orders list has no summary variant; a single small page is the
      // existing read. Its failure never fails the month's figures.
      getJsonOrNull<Record<string, unknown>>(
        apiEndpoints.getOrders(1, ACTIVITY_PAGE, undefined, '-created_at'),
      ),
      fetchChannelCounts(channels),
    ]);
  const [c, o, r] = [settled(calls), settled(orders), settled(reservations)];
  if (!c && !o && !r)
    throw new Error('This month’s figures could not be loaded.');
  const posStatus = settled(pos);
  const openTableStatus = settled(openTable);
  const gating: ProductGating = {
    orders: posStatus ? ordersStatusHasActiveConnection(posStatus) : null,
    reservations: openTableStatus
      ? reservationsStatusHasActiveConnection(openTableStatus)
      : null,
  };
  return {
    stats: readStats(c, o, r, settled(counts) ?? {}),
    gating,
    channels,
    activity: buildActivity(c, settled(recentOrders), r, gating),
  };
}

export function useDashboardFigures() {
  const { user } = useAuth();
  const nav = useNavVisibility();
  const channels: ChannelGating = { events: nav.showEvents, sms: nav.showSms };
  return useQuery({
    queryKey: useScopedQueryKey(
      'dashboard',
      'figures',
      channels.events,
      channels.sms,
    ),
    queryFn: () => fetchFigures(channels),
    enabled: Boolean(user),
    // Store flags resolving after the first read change the key; keep the
    // figures on screen instead of flashing the skeleton.
    placeholderData: keepPreviousData,
  });
}

/** Switches event requests on, as the Store info switch does. */
export function useTurnOnEvents() {
  const client = useQueryClient();
  const { user } = useAuth();
  const nav = useNavVisibility();
  const storeId =
    getCurrentStoreId() || (user?.store_id ? String(user.store_id) : '');
  const dashboardKey = useScopedQueryKey('dashboard');
  // The key the strip will read once events are on. Filling it before the
  // flag flips means that read is the live counts, not a refetch of Off.
  const enabledFiguresKey = useScopedQueryKey(
    'dashboard',
    'figures',
    true,
    nav.showSms,
  );
  return useMutation({
    mutationFn: async () => {
      const id = getCurrentStoreId();
      if (!id) throw new Error('Could not turn on.');
      const response = await api.put(apiEndpoints.updateOwnStore(id), {
        takes_events: true,
      });
      if (!response?.ok) throw new Error('Could not turn on.');
    },
    onSuccess: async () => {
      const channels: ChannelGating = { events: true, sms: nav.showSms };
      // The save has already stuck. A figures failure must not skip the flag
      // flip, or the card stays Off and the mutation looks like it failed.
      let loaded = false;
      try {
        client.setQueryData(enabledFiguresKey, await fetchFigures(channels));
        loaded = true;
      } catch {
        // Recovered below, once events are on.
      } finally {
        rememberEventsEnabled(client, user?.id, storeId);
        window.dispatchEvent(new Event('store-settings-updated'));
      }
      if (loaded) return;
      await Promise.all([
        client
          .invalidateQueries({ queryKey: dashboardKey })
          .catch(() => undefined),
        client
          .fetchQuery({
            queryKey: enabledFiguresKey,
            queryFn: () => fetchFigures(channels),
          })
          .catch(() => undefined),
      ]);
    },
  });
}

/** Saves the store's average revenue per guest and refreshes the figures. */
export function useUpdateReservationCover() {
  const client = useQueryClient();
  const figuresKey = useScopedQueryKey('dashboard', 'figures');
  return useMutation({
    mutationFn: async (amount: number) => {
      const storeId = getCurrentStoreId();
      if (!storeId) throw new Error('Could not save.');
      const response = await api.put(apiEndpoints.updateOwnStore(storeId), {
        average_reservation_cover: amount,
      });
      if (!response?.ok) throw new Error('Could not save.');
    },
    onSuccess: () => client.invalidateQueries({ queryKey: figuresKey }),
  });
}

// ---------------------------------------------------------------------------
// Call reasons
// ---------------------------------------------------------------------------

export function useCallIntelligence(scope: 'all_time' | 'month' = 'all_time') {
  const { user } = useAuth();
  return useQuery({
    queryKey: useScopedQueryKey('dashboard', 'call-intelligence', scope),
    queryFn: async () =>
      (await getJson<CallIntelligence | null>(
        apiEndpoints.getDashboardCallIntelligence(scope),
        'Call reasons',
      )) ?? null,
    enabled: Boolean(user),
  });
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export interface StorePulse {
  busyStatus: string | null;
  /** Store-local calendar date, `YYYY-MM-DD`. */
  todayLocal: string | null;
  storeId: number | null;
  /** Null when the store info payload did not include the check. */
  routingActive: boolean | null;
}

/** Store clock and whether calls are actually being answered. */
export function useStoreStatus() {
  const { user } = useAuth();
  return useQuery({
    queryKey: useScopedQueryKey('dashboard', 'store-status'),
    queryFn: async (): Promise<StorePulse> => {
      const info = await getJson<{
        busy_mode_status?: string | null;
        today_local_date?: string | null;
        store_id?: number | null;
        call_routing_active?: boolean | null;
      } | null>(apiEndpoints.getStoreInfo(), 'Store status');
      return {
        busyStatus: info?.busy_mode_status ?? null,
        todayLocal:
          typeof info?.today_local_date === 'string'
            ? info.today_local_date
            : null,
        storeId: typeof info?.store_id === 'number' ? info.store_id : null,
        routingActive:
          typeof info?.call_routing_active === 'boolean'
            ? info.call_routing_active
            : null,
      };
    },
    enabled: Boolean(user),
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Action items
// ---------------------------------------------------------------------------

interface NeedsYouSources {
  sms: boolean;
  events: boolean;
  catering: boolean;
  eventsPlatform: boolean;
  knowledgeQuestions: boolean;
  storeId: string;
}

async function listGroup<T>(
  kind: AttentionKind,
  url: string,
  toItem: (row: T) => AttentionGroup['items'][number],
): Promise<AttentionGroup> {
  const page = await getJson<Paginated<T>>(url, kind);
  const items = (page.results ?? []).slice(0, ROWS_PER_GROUP).map(toItem);
  return { kind, count: page.count ?? items.length, items };
}

/**
 * The events platform groups use the store's own venue scope, so the list
 * matches the store the dashboard is showing. A store outside every enabled
 * organization, or a role without the capability, simply has no group.
 */
async function platformGroups(
  client: QueryClient,
  storeId: string,
): Promise<{ scope: EventsScope; capabilities: string[] } | null> {
  const organizations = await client.fetchQuery({
    queryKey: eventsKeys.organizations(),
    queryFn: fetchEventsOrganizations,
    staleTime: 60_000,
  });
  const venueId = Number(storeId);
  const organization = organizations.find(
    (org) =>
      org.enabled &&
      !org.reconciliation_only &&
      org.venues.some((venue) => venue.id === venueId),
  );
  if (!organization) return null;
  const scope = { organizationId: organization.id, venueId };
  let session;
  try {
    session = await fetchEventsSession(scope);
  } catch (error) {
    // No events role at this venue: the groups don't apply, nothing failed.
    if (
      error instanceof EventsApiError &&
      (error.status === 403 || error.status === 404)
    ) {
      return null;
    }
    throw error;
  }
  if (!session.enabled || session.reconciliation_only) return null;
  return { scope, capabilities: session.capabilities };
}

async function fetchNeedsYou(
  client: QueryClient,
  sources: NeedsYouSources,
): Promise<NeedsYou> {
  const requests: [AttentionKind, Promise<AttentionGroup>][] = [];

  requests.push([
    'voicemail',
    listGroup<ApiVoicemail>(
      'voicemail',
      apiEndpoints.voicemails({
        folder: 'inbox',
        unread: true,
        page: 1,
        page_size: ROWS_PER_GROUP,
      }),
      voicemailItem,
    ),
  ]);
  if (sources.sms) {
    requests.push([
      'sms',
      listGroup<ApiSmsConversation>(
        'sms',
        apiEndpoints.smsConversations({
          needs_human: true,
          page: 1,
          page_size: ROWS_PER_GROUP,
        }),
        smsItem,
      ),
    ]);
  }
  if (sources.events) {
    requests.push([
      'event_inquiry',
      listGroup<ApiInquiry>(
        'event_inquiry',
        apiEndpoints.getEventInquiries({
          dashboard_state: 'unseen',
          page: 1,
          page_size: ROWS_PER_GROUP,
        }),
        (row) => inquiryItem('event_inquiry', row),
      ),
    ]);
  }
  if (sources.catering) {
    requests.push([
      'catering_inquiry',
      listGroup<ApiInquiry>(
        'catering_inquiry',
        apiEndpoints.getCateringInquiries({
          dashboard_state: 'unseen',
          page: 1,
          page_size: ROWS_PER_GROUP,
        }),
        (row) => inquiryItem('catering_inquiry', row),
      ),
    ]);
  }
  if (sources.knowledgeQuestions) {
    requests.push([
      'knowledge_question',
      listGroup<ApiKnowledgeQuestion>(
        'knowledge_question',
        apiEndpoints.knowledgeQuestions({
          status: 'open',
          page: 1,
          page_size: ROWS_PER_GROUP,
        }),
        knowledgeQuestionItem,
      ),
    ]);
  }

  if (sources.eventsPlatform && sources.storeId) {
    const platform = platformGroups(client, sources.storeId);
    requests.push([
      'payment',
      platform.then(async (p) => {
        if (!p?.capabilities.includes('crm.read')) {
          return { kind: 'payment', count: 0, items: [] };
        }
        const bookings = paymentBookings(await fetchPipeline(p.scope));
        return {
          kind: 'payment',
          count: bookings.length,
          items: bookings.slice(0, ROWS_PER_GROUP).map(paymentItem),
        };
      }),
    ]);
    requests.push([
      'approval',
      platform.then(async (p) => {
        if (!p?.capabilities.includes('approval.read')) {
          return { kind: 'approval', count: 0, items: [] };
        }
        const pending = [...(await fetchApprovals(p.scope, 'PENDING'))].sort(
          (a, z) => z.created_at.localeCompare(a.created_at),
        );
        return {
          kind: 'approval',
          count: pending.length,
          items: pending.slice(0, ROWS_PER_GROUP).map(approvalItem),
        };
      }),
    ]);
  }

  const results = await Promise.allSettled(requests.map(([, p]) => p));
  const groups: AttentionGroup[] = [];
  const failed: AttentionKind[] = [];
  results.forEach((result, i) => {
    if (result.status === 'fulfilled') groups.push(result.value);
    else failed.push(requests[i][0]);
  });
  if (groups.length === 0) throw new Error('Action items could not be loaded.');
  return { groups, failed };
}

export function useNeedsYou() {
  const { user } = useAuth();
  const nav = useNavVisibility();
  const client = useQueryClient();
  const sources: NeedsYouSources = {
    sms: nav.showSms,
    events: nav.showEvents,
    catering: nav.showCatering,
    eventsPlatform: nav.showEventsPlatform,
    knowledgeQuestions: nav.showKnowledgeQuestions,
    storeId:
      getCurrentStoreId() ||
      (user?.store_id != null ? String(user.store_id) : ''),
  };
  return useQuery({
    queryKey: useScopedQueryKey(
      'dashboard',
      'needs-you',
      sources.sms,
      sources.events,
      sources.catering,
      sources.eventsPlatform,
      sources.knowledgeQuestions,
    ),
    queryFn: () => fetchNeedsYou(client, sources),
    enabled: Boolean(user),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // Store flags resolving after the first read change the key; keep the
    // rows on screen instead of flashing the skeleton.
    placeholderData: keepPreviousData,
  });
}
